import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  InputGroup,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import api from "../../../services/api"; // axios instance
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";

export default function CouponsManager() {
  const [query, setQuery] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/coupons");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.code,
        r.type,
        r.campaignName,
        r.description ?? "",
        String(r.discountPercentage ?? ""),
        String(r.discountAmount ?? ""),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const upsert = async (payload) => {
    // If id present → PUT; else → POST
    if (payload.id) {
      const { data } = await api.put(
        `/api/admin/coupons/${payload.id}`,
        payload
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
    } else {
      const { data } = await api.post(`/api/admin/coupons`, payload);
      setRows((prev) => [data, ...prev]);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/api/admin/coupons/${id}`);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      <Card className="shadow-sm mb-3">
        <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div className="fw-semibold">Manage Coupons</div>
          <div className="d-flex gap-2">
            <InputGroup size="sm">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="code, type, description"
              />
            </InputGroup>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setShow(true);
              }}
            >
              + New
            </Button>
          </div>
        </Card.Header>

        {loading ? (
          <div className="py-5 text-center">
            <Spinner /> <div className="small text-muted mt-2">Loading…</div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr><th style={{ width: 44 }}>#</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Campaign</th>
                  <th className="text-end">Discount</th>
                  <th className="text-end">Min Cart</th>
                  <th>Expiry</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, index) => (
                  <tr key={r.id}><td className="small fw-semibold">{index + 1}</td>
                    <td className="fw-semibold">{r.code}</td>
                    <td className="text-capitalize">{r.type?.toLowerCase()}</td>
                    <td className="text-truncate" style={{ maxWidth: 140 }}>
                      {r.campaignName || "-"}
                    </td>
                    <td className="text-end">
                      {r.discountPercentage != null
                        ? `${r.discountPercentage}%`
                        : r.discountAmount != null
                        ? `₹${r.discountAmount}`
                        : "-"}
                    </td>
                    <td className="text-end">
                      {r.minimumCartValue != null
                        ? `₹${r.minimumCartValue}`
                        : "-"}
                    </td>
                    <td className="text-nowrap">{formatDateDMY(r.expiryDate)}</td>
                    <td>
                      {r.usedCount ?? 0}/{r.usageLimit ?? "-"}
                    </td>
                    <td>
                      <Badge bg={r.isActive ? "success" : "secondary"}>
                        {r.isActive ? "Active" : "Inactive"}
                      </Badge>
                      {r.autoApply ? (
                        <Badge bg="info" className="ms-1">
                          Auto
                        </Badge>
                      ) : null}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => {
                          setEditing(r);
                          setShow(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => onDelete(r.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      No coupons found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <CouponModal
        show={show}
        onHide={() => setShow(false)}
        initial={editing}
        onSave={async (v) => {
          await upsert(v);
          setShow(false);
          setEditing(null);
        }}
      />
    </>
  );
}

function CouponModal({ show, onHide, initial, onSave }) {
  const empty = {
    id: undefined,
    code: "",
    type: "DEFAULT",
    campaignName: "",
    discountPercentage: "10", // keep as string for inputs
    discountAmount: "", // keep as string
    minimumCartValue: "3000",
    expiryDate: "",
    isActive: true,
    usageLimit: "100",
    usedCount: "0",
    autoApply: false,
    description: "",
  };

  const deriveInitialMode = (c) => {
    if (!c) return "percent";
    if (c.discountAmount != null && Number(c.discountAmount) > 0) return "flat";
    return "percent";
  };

  const normalize = (c) => {
    if (!c) return { ...empty };
    return {
      ...empty,
      ...c,
      code: (c.code || "").toUpperCase(),
      discountPercentage:
        c.discountPercentage != null ? String(c.discountPercentage) : "",
      discountAmount: c.discountAmount != null ? String(c.discountAmount) : "",
      minimumCartValue:
        c.minimumCartValue != null ? String(c.minimumCartValue) : "",
      usageLimit: c.usageLimit != null ? String(c.usageLimit) : "",
      usedCount: c.usedCount != null ? String(c.usedCount) : "0",
      expiryDate: c.expiryDate ? String(c.expiryDate).slice(0, 10) : "",
      isActive: !!c.isActive,
      autoApply: !!c.autoApply,
    };
  };

  const [form, setForm] = useState(normalize(initial));
  const [discountMode, setDiscountMode] = useState(deriveInitialMode(initial));

  useEffect(() => {
    if (show) {
      setForm(normalize(initial));
      setDiscountMode(deriveInitialMode(initial)); // set once when opening
    }
  }, [initial, show]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.code.trim()) return alert("Coupon code is required");

    // parse numbers safely ("" -> null)
    const pct =
      form.discountPercentage === "" ? null : Number(form.discountPercentage);
    const amt = form.discountAmount === "" ? null : Number(form.discountAmount);
    const min =
      form.minimumCartValue === "" ? null : Number(form.minimumCartValue);
    const limit = form.usageLimit === "" ? null : Number(form.usageLimit);
    const used = form.usedCount === "" ? 0 : Number(form.usedCount);

    // enforce based on selected mode (no auto flip)
    if (discountMode === "percent") {
      if (!pct || pct <= 0)
        return alert("Enter a valid discount percentage (> 0).");
    } else {
      if (!amt || amt <= 0)
        return alert("Enter a valid flat discount amount (> 0).");
    }

    onSave({
      ...form,
      discountPercentage: discountMode === "percent" ? pct : null,
      discountAmount: discountMode === "flat" ? amt : null,
      minimumCartValue: min,
      usageLimit: limit,
      usedCount: used,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? "Edit Coupon" : "New Coupon"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="vstack gap-2">
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={form.code}
                onChange={(e) => update("code", e.target.value.toUpperCase())}
                required
                placeholder="e.g., IO10"
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.type}
                onChange={(e) => update("type", e.target.value)}
              >
                <option value="DEFAULT">DEFAULT</option>
                <option value="REFERRAL">REFERRAL</option>
                <option value="CAMPAIGN">CAMPAIGN</option>
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Campaign Name (optional)</Form.Label>
              <Form.Control
                value={form.campaignName || ""}
                onChange={(e) => update("campaignName", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Discount Mode</Form.Label>
              <Form.Select
                value={discountMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setDiscountMode(mode);
                  // don’t infer mode from values anymore; just clear the opposite field
                  if (mode === "percent") update("discountAmount", "");
                  else update("discountPercentage", "");
                }}
              >
                <option value="percent">Percentage (%)</option>
                <option value="flat">Flat (₹)</option>
              </Form.Select>
            </Col>
          </Row>

          <Row>
            {discountMode === "percent" ? (
              <Col md={6} className="mb-2">
                <Form.Label>Discount %</Form.Label>
                <InputGroup>
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min={1}
                    max={100}
                    value={form.discountPercentage}
                    onChange={(e) =>
                      update("discountPercentage", e.target.value)
                    }
                  />
                </InputGroup>
              </Col>
            ) : (
              <Col md={6} className="mb-2">
                <Form.Label>Discount Amount (₹)</Form.Label>
                <InputGroup>
                  <InputGroup.Text>₹</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.discountAmount}
                    onChange={(e) => update("discountAmount", e.target.value)}
                  />
                </InputGroup>
              </Col>
            )}

            <Col md={6} className="mb-2">
              <Form.Label>Min Cart Value (₹)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={form.minimumCartValue}
                onChange={(e) => update("minimumCartValue", e.target.value)}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Expiry Date</Form.Label>
              <IsoDatePicker
                value={form.expiryDate}
                onChange={(value) => update("expiryDate", value)}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Usage Limit</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={form.usageLimit}
                onChange={(e) => update("usageLimit", e.target.value)}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Used Count</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={form.usedCount}
                onChange={(e) => update("usedCount", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-2 d-flex align-items-end gap-3">
              <Form.Check
                type="switch"
                id="sw-active"
                label="Active"
                checked={!!form.isActive}
                onChange={(e) => update("isActive", e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="sw-auto"
                label="Auto-apply"
                checked={!!form.autoApply}
                onChange={(e) => update("autoApply", e.target.checked)}
              />
            </Col>
          </Row>

          <Form.Label>Description (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
